import {
  PairedCostRouteItem,
  alertRequiresNavAttention,
  areEquivalentAlertPages,
  getAlertPageKey,
  getOrderedTriggeredAlertsForToasts,
} from '@aloft/toolkit-shared';

import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAlertsRef } from '../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCommentaryV2 } from '../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectAlerts } from '../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import { selectIsInspectifyOrder } from '../redux/features/selectors/orderSelectors.ts';
import { selectReferenceBarUI } from '../redux/features/selectors/referenceBarUISelectors.ts';
import {
  selectPendingNavBlockToastTypes,
  selectToastSuppressedAlertTypes,
  selectTriggeredAlerts,
} from '../redux/features/selectors/uiSelectors.ts';
import { clearPendingNavBlockToastTypes } from '../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';
import { applyVisibleAlertToasts } from './useToaster/applyVisibleAlertToasts.ts';
import { dismissInactiveAlertToasts } from './useToaster/dismissAllToasts.ts';
import { useToaster } from './useToaster/useToaster.tsx';
import { routeToStepMap } from './validation/consts.ts';
import { useAppraisifyNavigation } from './validation/useAppraisifyNavigation.ts';

export const shouldShowWarningAlertToastOnRoute = ({
  alertPage,
  currentRoute,
  expectedPreviousRoute,
  previousVisitedRoute,
  isPendingOnCurrentPage = false,
}: {
  alertPage: ReturnType<typeof getAlertPageKey>;
  currentRoute?: PairedCostRouteItem;
  expectedPreviousRoute?: PairedCostRouteItem;
  previousVisitedRoute?: PairedCostRouteItem;
  isPendingOnCurrentPage?: boolean;
}) =>
  Boolean(
    currentRoute &&
      ((isPendingOnCurrentPage && areEquivalentAlertPages(alertPage, currentRoute)) ||
        (expectedPreviousRoute &&
          previousVisitedRoute &&
          !areEquivalentAlertPages(alertPage, currentRoute) &&
          areEquivalentAlertPages(alertPage, expectedPreviousRoute) &&
          areEquivalentAlertPages(alertPage, previousVisitedRoute))),
  );

export const useTriggeredAlertToasts = () => {
  const dispatch = useAppDispatch();
  const makeToast = useToaster();
  const triggeredAlerts = useAppSelector(selectTriggeredAlerts);
  const pendingNavBlockToastTypes = useAppSelector(selectPendingNavBlockToastTypes);
  const toastSuppressedAlertTypes = useAppSelector(selectToastSuppressedAlertTypes);
  const { referenceViewOpen, selectedDataSheet } = useAppSelector(selectReferenceBarUI);
  const isAlertsPanelOpen =
    referenceViewOpen && selectedDataSheet === 'craftsman-alerts';
  const suppressedAlertTypes = useMemo(
    () => new Set(toastSuppressedAlertTypes),
    [toastSuppressedAlertTypes],
  );
  const pendingNavBlockToastTypeSet = useMemo(
    () => new Set(pendingNavBlockToastTypes),
    [pendingNavBlockToastTypes],
  );
  const triggeredAlertTypeSet = useMemo(() => new Set(triggeredAlerts), [triggeredAlerts]);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = { dismissedAlerts, finalizedExplanations };
  const alertsRef = useAlertsRef();
  const inspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const { pathname } = useLocation();
  const { getCurrentAppraisifyRoute, getPreviousAppraisifyRoute, isRouteWithinAlertNavFrontier } =
    useAppraisifyNavigation();
  const currentAppraisifyRoute = getCurrentAppraisifyRoute(pathname);
  const previousExpectedAppraisifyRoute = currentAppraisifyRoute
    ? getPreviousAppraisifyRoute(currentAppraisifyRoute)
    : undefined;
  const previousVisitedAppraisifyRouteRef = useRef<typeof currentAppraisifyRoute>();

  useEffect(() => {
    if (!inspectifyOrder) return;

    if (isAlertsPanelOpen || triggeredAlerts.length === 0) {
      dismissInactiveAlertToasts(new Set());
      return;
    }

    const { adjustments, comparable, depreciatedCost } = alertsRef.current;
    const alertsPerSection = { adjustments, comparable, depreciatedCost };

    const orderedTriggeredAlerts = getOrderedTriggeredAlertsForToasts(
      alertsPerSection,
      triggeredAlerts,
      routeToStepMap,
      alertsCommentary,
      explanationContext,
    );
    const flatAlerts = Object.values(alertsPerSection).flat();
    const alertsToToast =
      orderedTriggeredAlerts.length > 0
        ? orderedTriggeredAlerts
        : triggeredAlerts.flatMap((type) =>
            flatAlerts.filter(
              (alert) => alert.type === type && alertRequiresNavAttention(alert, alertsCommentary, explanationContext),
            ),
          );

    const pendingOnCurrentPage =
      currentAppraisifyRoute &&
      pendingNavBlockToastTypes.some((type) => {
        const alert = flatAlerts.find((candidate) => candidate.type === type);
        return (
          alert &&
          areEquivalentAlertPages(getAlertPageKey(alert, routeToStepMap), currentAppraisifyRoute)
        );
      });

    const visibleAlerts = (
      currentAppraisifyRoute
        ? alertsToToast.filter((alert) => {
            if (
              pendingNavBlockToastTypeSet.has(alert.type) &&
              triggeredAlertTypeSet.has(alert.type)
            ) {
              return true;
            }
            const alertPage = getAlertPageKey(alert, routeToStepMap);
            if (alert.severity === 'warning') {
              return shouldShowWarningAlertToastOnRoute({
                alertPage,
                currentRoute: currentAppraisifyRoute,
                expectedPreviousRoute: previousExpectedAppraisifyRoute,
                previousVisitedRoute: previousVisitedAppraisifyRouteRef.current,
                isPendingOnCurrentPage: pendingNavBlockToastTypeSet.has(alert.type),
              });
            }
            if (areEquivalentAlertPages(alertPage, currentAppraisifyRoute)) {
              return Boolean(pendingOnCurrentPage && triggeredAlertTypeSet.has(alert.type));
            }
            return isRouteWithinAlertNavFrontier(
              alertPage,
              currentAppraisifyRoute,
              alertsPerSection,
            );
          })
        : alertsToToast.filter((alert) => pendingNavBlockToastTypeSet.has(alert.type))
    ).filter((alert) => !suppressedAlertTypes.has(alert.type));

    if (visibleAlerts.length === 0) {
      if (triggeredAlerts.length === 0) {
        dismissInactiveAlertToasts(new Set());
      }
      return;
    }

    applyVisibleAlertToasts(visibleAlerts, makeToast);

    if (visibleAlerts.some((alert) => pendingNavBlockToastTypeSet.has(alert.type))) {
      dispatch(clearPendingNavBlockToastTypes());
    }
  }, [
    alertsCommentary,
    currentAppraisifyRoute,
    dispatch,
    inspectifyOrder,
    isAlertsPanelOpen,
    isRouteWithinAlertNavFrontier,
    makeToast,
    pendingNavBlockToastTypeSet,
    pendingNavBlockToastTypes.length,
    previousExpectedAppraisifyRoute,
    suppressedAlertTypes,
    triggeredAlerts,
  ]);

  useEffect(() => {
    previousVisitedAppraisifyRouteRef.current = currentAppraisifyRoute;
  }, [currentAppraisifyRoute]);
};
